<div class="ws-step">
  <div class="ws-step-header">
    @if (settingUpTrialAccount) {
      <h3>Set up a trial account</h3>
      <p class="text-muted">Enter the prospect's details and their business info. We'll customize the CRM using AI.</p>
    } @else {
      <h3>Let's set up a Workspace just for you — <strong>in 2 minutes</strong></h3>
      <p class="text-muted" style="font-size: 15px;">Tell us about your business and we'll customize everything for you using AI.</p>
    }
  </div>

  <section class="ws-trial-reward-banner" aria-label="Trial reward">
    <span class="ws-trial-reward-banner__icon" aria-hidden="true"><i class="bi bi-gift-fill"></i></span>
    <div class="ws-trial-reward-banner__copy">
      <span class="ws-trial-reward-banner__eyebrow">Your trial reward</span>
      <h4>Earn one admin seat free for your first year</h4>
      <p>Add 3 sales reps, install Salesmax on all 3 phones, and log 500 tracked calls before your trial ends.</p>
      <small>For self-created trials. Support confirms eligibility when you subscribe.</small>
    </div>
    <div class="ws-trial-reward-banner__targets" aria-label="Reward targets">
      <span><strong>3</strong> reps</span>
      <span><strong>3</strong> installs</span>
      <span><strong>500</strong> calls</span>
    </div>
  </section>

  <!-- Trial account: prospect signup fields -->
  @if (settingUpTrialAccount) {
    <div class="ws-trial-signup mb-4 p-3 border rounded bg-light">
      <h6 class="mb-3"><i class="bi bi-person-plus me-1"></i> Prospect Details</h6>
      <form [formGroup]="signUpForm">
        <div class="mb-3">
          <label class="form-label fw-medium">Full Name</label>
          <input class="form-control" formControlName="fullName" placeholder="John Doe" autocomplete="name" />
          @if (signUpForm.controls['fullName'].invalid && signUpForm.controls['fullName'].touched) {
            <small class="text-danger">Full name is required</small>
          }
        </div>
        <div class="mb-3">
          <label class="form-label fw-medium">Email</label>
          <input class="form-control" formControlName="email" placeholder="john.doe@business.com" autocomplete="email"
            (blur)="signUpForm.controls['email'].setValue(signUpForm.controls['email'].value?.trim())" />
          @if (signUpForm.controls['email'].invalid && signUpForm.controls['email'].touched) {
            <small class="text-danger">Please enter a valid email address</small>
          }
        </div>
        <div class="mb-3">
          <label class="form-label fw-medium">Phone Number</label>
          <ngx-intl-tel-input
            [preferredCountries]="preferredCountries"
            [enableAutoCountrySelect]="true"
            [enablePlaceholder]="true"
            [searchCountryFlag]="true"
            [searchCountryField]="[SearchCountryField.Iso2, SearchCountryField.Name]"
            [selectFirstCountry]="false"
            [selectedCountryISO]="CountryISO.India"
            [maxLength]="15"
            [phoneValidation]="true"
            [separateDialCode]="separateDialCode"
            [numberFormat]="PhoneNumberFormat.National"
            formControlName="sms">
          </ngx-intl-tel-input>
          @if (signUpForm.controls['sms'].invalid && signUpForm.controls['sms'].touched) {
            <small class="text-danger">Please enter a valid phone number</small>
          }
        </div>
      </form>
    </div>
  }

  @if (settingUpTrialAccount && signUpForm.invalid) {
    <div class="ws-step-body text-center text-muted py-4">
      <i class="bi bi-arrow-up-circle d-block mb-2" style="font-size: 28px;"></i>
      Please complete the prospect details above to continue.
    </div>
  } @else {
  <div class="ws-step-body">
    @switch (mode) {
      <!-- Step 1: Choose how to set up -->
      @case ('choose') {
        <p class="mb-4 fw-medium">How would you like to get started?</p>
        <div class="d-flex gap-3">
          <div class="ws-item-card flex-fill text-center p-4" style="cursor: pointer;" (click)="selectMode('website')">
            <i class="bi bi-globe d-block mb-2" style="font-size: 32px; color: #5c6bc0;"></i>
            <div class="fw-bold">I have a website</div>
            <small class="text-muted">We'll extract your business info automatically using AI</small>
          </div>
          <div class="ws-item-card flex-fill text-center p-4" style="cursor: pointer;" (click)="selectMode('manual')">
            <i class="bi bi-pencil-square d-block mb-2" style="font-size: 32px; color: #5c6bc0;"></i>
            <div class="fw-bold">I don't have a website</div>
            <small class="text-muted">Enter your business name and description manually</small>
          </div>
        </div>
      }

      <!-- Step 2a: Website mode — enter URLs -->
      @case ('website') {
        @if (!extractedData) {
          <!-- URL entry form -->
          <div class="mb-4">
            <label class="form-label fw-bold">Your business website</label>
            <input class="form-control" [(ngModel)]="websiteUrl"
              placeholder="e.g. https://www.yourbusiness.com" />
          </div>

          <div class="mb-4">
            <label class="form-label fw-bold">Other listing pages (optional)</label>
            <small class="text-muted d-block mb-2">Google Maps, JustDial, IndiaMART, social media, etc. — helps us get more details.</small>
            @for (url of additionalUrls; track trackByIndex($index); let i = $index) {
              <div class="d-flex gap-2 mb-2">
                <input class="form-control" [(ngModel)]="additionalUrls[i]"
                  placeholder="e.g. https://maps.google.com/..." />
                @if (additionalUrls.length > 1) {
                  <button class="btn btn-sm btn-outline-danger" (click)="removeUrlField(i)"><i class="bi bi-x-lg"></i></button>
                }
              </div>
            }
            <button class="btn btn-sm btn-link p-0" (click)="addUrlField()">
              <i class="bi bi-plus-lg me-1"></i>Add another URL
            </button>
          </div>

          @if (isExtracting) {
            <div class="text-center py-4">
              <div class="spinner-border text-primary mb-3" role="status"></div>
              <p class="fw-medium text-primary mb-1">{{ extractionMessage }}</p>
              <small class="text-muted">This may take up to a minute. Please don't close this page.</small>
            </div>
          } @else {
            <div class="d-flex gap-2">
              <button class="btn btn-outline-secondary" (click)="mode = 'choose'">Back</button>
              <button mat-raised-button color="primary" (click)="extractFromWebsite()"
                [disabled]="!websiteUrl.trim()">
                <i class="bi bi-magic me-1"></i> Extract Business Info
              </button>
            </div>
          }
        }

        <!-- Extracted data review -->
        @if (extractedData) {
          <div class="mb-3 p-3 bg-success-subtle rounded">
            <i class="bi bi-check-circle text-success me-2 mr-2"></i>
            <strong>Business info extracted from your website</strong>
          </div>

          <!-- Logo preview -->
          @if (extractedData.logoUrl) {
            <div class="mb-4 d-flex align-items-center gap-3">
              <img [src]="extractedData.logoUrl" alt="Business logo"
                style="max-height: 64px; max-width: 200px; object-fit: contain; border-radius: 8px; border: 1px solid #eee; padding: 4px;"
                (error)="extractedData.logoUrl = null" />
              <small class="text-muted">Logo found on your website</small>
            </div>
          }

          <div class="mb-4">
            <label class="form-label fw-bold">Business Name</label>
            <input class="form-control" [(ngModel)]="businessName" />
          </div>

          <div class="mb-4">
            <label class="form-label fw-bold">Business Description</label>
            <textarea class="form-control" [(ngModel)]="businessDescription" rows="3"></textarea>
            <small class="text-muted">This helps our AI customize your CRM — deal stages, qualification fields, products, and more.</small>
          </div>

          <ng-container *ngTemplateOutlet="gstinField"></ng-container>

          @if (extractedData.location) {
            <div class="mb-4">
              <label class="form-label fw-bold text-muted">Location</label>
              <span class="ms-2">{{ extractedData.location }}</span>
            </div>
          }

          @if (extractedData.products?.length) {
            <div class="mb-4">
              <label class="form-label fw-bold text-muted">Detected products/services</label>
              <div class="d-flex flex-wrap gap-2">
                @for (product of extractedData.products; track product) {
                  <span class="badge bg-primary-subtle text-primary">{{ product }}</span>
                }
              </div>
            </div>
          }

          <div class="mb-4">
            <label class="form-label fw-bold">Upload logo (optional)</label>
            @if (extractedData.logoUrl) {
              <small class="text-muted d-block mb-2">We'll use the logo from your website. Upload below only if you want a different one.</small>
            }
            <uppy [plugins]="getUppyPlugins()" [on]="subject"></uppy>
          </div>

          <div class="d-flex justify-content-end gap-2">
            <button class="btn btn-outline-secondary" (click)="extractedData = null">Re-enter URLs</button>
            <button mat-raised-button color="primary" (click)="onSubmit()" [disabled]="!canSubmit">
              @if (isSaving) {
                <span class="spinner-border spinner-border-sm me-1"></span>
              }
              {{ settingUpTrialAccount ? 'Create Trial Account' : 'Create Organization' }}
            </button>
          </div>
        }
      }

      <!-- Step 2b: Manual mode -->
      @case ('manual') {
        <div class="mb-4">
          <label class="form-label fw-bold">Business Name</label>
          <input class="form-control" [(ngModel)]="businessName" placeholder="e.g. Sunshine Realty" />
        </div>

        <div class="mb-4">
          <label class="form-label fw-bold">Describe your business in 1-2 sentences</label>
          <textarea class="form-control" [(ngModel)]="businessDescription" rows="3"
            placeholder="e.g. We are a real estate agency that helps clients buy and sell residential properties in Mumbai. We focus on premium apartments and villas."
          ></textarea>
          <small class="text-muted">This helps our AI customize your CRM — call outcomes, deal stages, products, and more.</small>
        </div>

        <ng-container *ngTemplateOutlet="gstinField"></ng-container>

        <div class="mb-4">
          <label class="form-label fw-bold">Upload logo (optional)</label>
          <uppy [plugins]="getUppyPlugins()" [on]="subject"></uppy>
        </div>

        <div class="d-flex gap-2">
          <button class="btn btn-outline-secondary" (click)="mode = 'choose'">Back</button>
          <div class="flex-fill"></div>
          <button mat-raised-button color="primary" (click)="onSubmit()" [disabled]="!canSubmit">
            @if (isSaving) {
              <span class="spinner-border spinner-border-sm me-1"></span>
            }
            {{ settingUpTrialAccount ? 'Create Trial Account' : 'Create Organization' }}
          </button>
        </div>
      }
    }
  </div>
  }
</div>

<ng-template #gstinField>
  <div class="mb-4">
    <label class="form-label fw-bold">GST number (optional)</label>
    <input class="form-control" [(ngModel)]="gstin" maxlength="15" autocomplete="off"
      placeholder="e.g. 27ABCDE1234F1Z5"
      (ngModelChange)="gstin = $event?.toUpperCase()" />
    @if (gstinInvalid) {
      <small class="text-danger">Please enter a valid 15-character GSTIN (e.g., 27ABCDE1234F1Z5)</small>
    } @else {
      <small class="text-muted">Used on your Salesmax subscription invoices. You can add or change it later from Help → Update Tax ID.</small>
    }
  </div>
</ng-template>
